import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useBabackSupport } from "@babackapp/private-api-client/client/use-baback-support";
import useTranslation from "@babackapp/translations/use-translation";
import { useCallback, useMemo } from "react";

export interface ShopPersonUser {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  logoUrl?: string | null;
}

export interface ShopPerson {
  userId: string;
  user: ShopPersonUser;
  /** A Baback super admin, not in the shop team (BAB-3035). */
  isBabackSupport: boolean;
  /**
   * Can be assigned or mentioned: every member, and a super admin with the
   * flag and MFA today. A revoked one is still named on past actions.
   */
  isActive: boolean;
  /** "First Last", the email, or "First Last (Support Baback)". */
  displayName: string;
}

/**
 * Everyone who can act on the shop: its members, then the Baback support
 * (current and former super admins) who are not members. Activity authors,
 * assignees and mentions resolve against it, a super admin never being in
 * `shop.users`.
 */
export function useShopPeople(): ShopPerson[] {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { data: support } = useBabackSupport(shop.id);

  return useMemo(
    () =>
      buildShopPeople(shop.users, support ?? [], {
        supportLabel: t("superAdmin.supportLabel"),
        unknownUserLabel: t("superAdmin.unknownUser"),
      }),
    [shop.users, support, t],
  );
}

// "First Last", or whichever is set, else the email.
function personName(user: ShopPersonUser): string | null {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name || user.email || null;
}

/** Members first, then the Baback support who are not members. */
export function buildShopPeople(
  members: { userId: string; user?: ShopPersonUser | null }[],
  support: {
    userId: string;
    /** Sent to super admins only. */
    email?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    isActive: boolean;
  }[],
  labels: { supportLabel: string; unknownUserLabel: string },
): ShopPerson[] {
  // A member whose account is gone is kept, so it can still be unassigned.
  const memberPeople: ShopPerson[] = members.map((member) => ({
    userId: member.userId,
    user: member.user ?? { id: member.userId },
    isBabackSupport: false,
    isActive: true,
    displayName:
      (member.user && personName(member.user)) || labels.unknownUserLabel,
  }));
  const memberIds = new Set(members.map((member) => member.userId));
  const supportPeople: ShopPerson[] = support
    .filter((person) => !memberIds.has(person.userId))
    .map((person) => ({
      userId: person.userId,
      user: {
        id: person.userId,
        firstName: person.firstName,
        lastName: person.lastName,
        email: person.email,
      },
      isBabackSupport: true,
      isActive: person.isActive,
      displayName: personName(person)
        ? `${personName(person)} (${labels.supportLabel})`
        : labels.supportLabel,
    }));

  return [...memberPeople, ...supportPeople];
}

/**
 * Each id as a person: one no longer resolved (left the team, support history
 * past a year) is shown as unknown, so it can still be unassigned.
 */
export function resolvePeople(
  people: ShopPerson[],
  userIds: string[],
  unknownUserLabel: string,
): ShopPerson[] {
  return userIds.map(
    (userId) =>
      people.find((person) => person.userId === userId) ?? {
        userId,
        user: { id: userId },
        isBabackSupport: false,
        isActive: false,
        displayName: unknownUserLabel,
      },
  );
}

/** Resolves a user id to a display name, members and Baback support alike. */
export function useActorName() {
  const people = useShopPeople();
  const byId = useMemo(
    () => new Map(people.map((person) => [person.userId, person])),
    [people],
  );

  return useCallback(
    (userId?: string | null, fallback: string | null = null) =>
      (userId && byId.get(userId)?.displayName) || fallback,
    [byId],
  );
}
